/**
 * A current (unexpired) Auth0 ID token for the gateway login.
 *
 * `getIdTokenClaims()` returns whatever ID token is cached — with
 * `cacheLocation="localstorage"` that can be hours past its `exp`, and the
 * gateway rejects it ("Token has expired"), leaving a returning traveller
 * unable to chat. So: use the cached token only while it has a margin left;
 * otherwise ask Auth0 for fresh tokens (`cacheMode: 'off'`); if that fails
 * (e.g. Safari blocking the silent-auth iframe's third-party cookie), send the
 * traveller to sign in again rather than surfacing an error.
 */

export type IdTokenClaimsLike = { __raw?: string; exp?: number } | undefined;

export type FreshIdTokenDeps = {
	getIdTokenClaims: () => Promise<IdTokenClaimsLike>;
	getAccessTokenSilently: (options: { cacheMode: 'off' }) => Promise<unknown>;
	loginWithRedirect: () => Promise<void>;
	now?: () => number;
};

/** Seconds of remaining validity below which a token is treated as expired. */
export const ID_TOKEN_MIN_REMAINING_SECONDS = 60;

/** Thrown after redirecting to sign-in; the caller should stop, not report an error. */
export class ReauthenticationRedirectError extends Error {
	constructor() {
		super('Your sign-in expired. Redirecting you to sign in again.');
		this.name = 'ReauthenticationRedirectError';
	}
}

export const isIdTokenFresh = (claims: IdTokenClaimsLike, nowMs: number): boolean => {
	const raw = String(claims?.__raw || '').trim();
	const exp = Number(claims?.exp);
	if (!raw || !Number.isFinite(exp)) return false;
	return exp * 1000 - nowMs > ID_TOKEN_MIN_REMAINING_SECONDS * 1000;
};

export const getFreshIdToken = async (deps: FreshIdTokenDeps): Promise<string> => {
	const now = deps.now ?? Date.now;

	const cached = await deps.getIdTokenClaims();
	if (isIdTokenFresh(cached, now())) return String(cached!.__raw).trim();

	try {
		// Re-authenticates silently and replaces the cached ID token too.
		await deps.getAccessTokenSilently({ cacheMode: 'off' });
		const refreshed = await deps.getIdTokenClaims();
		if (isIdTokenFresh(refreshed, now())) return String(refreshed!.__raw).trim();
	} catch {
		// Fall through to an interactive sign-in.
	}

	await deps.loginWithRedirect();
	throw new ReauthenticationRedirectError();
};
